import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { Provider } from 'react-redux';

import {
  ApiClient,
  JWTClientConstraints,
  JWTHttpClient,
  UnauthenticatedClientConstraints,
  UnauthenticatedHttpClient,
} from '@provider-portal/common/api';
import { FullViewSpinner } from '@provider-portal/common/components/FullViewSpinner/FullViewSpinner';
import config from '@provider-portal/common/config';
import {
  ProviderPortalModuleConfig,
  ProviderPortalModuleSetup,
} from '@provider-portal/common/Module';
import { AccountService } from '@provider-portal/common/services/AccountService';
import { BrazeService } from '@provider-portal/common/services/BrazeService';
import { CommonApplicationServices } from '@provider-portal/common/services/CommonApplicationServices';
import { EventAliasingService } from '@provider-portal/common/services/EventAliasingService';
import { MixpanelService } from '@provider-portal/common/services/MixpanelService';
import { NotificationService } from '@provider-portal/common/services/NotificationService';
import { ProviderPortalNavigationService } from '@provider-portal/common/services/ProviderPortalNavigationService';
import { TrackingService } from '@provider-portal/common/services/TrackingService';
import { PersistGate } from 'redux-persist/integration/react';

import { HttpClientBoltServer } from '@bolteu/http-client-bolt-server';
import { App, Facade, ModuleConfig, RenderProps } from '@bolteu/lattice';
import { waitForAccessTokenUpdate } from '@bolteu/redux-session';

import { createAuthInvalidatedInterceptor } from 'api/authInvalidatedInterceptor';
import { createGatewaySelectors } from 'api/selectors';
import { AppServices } from 'rdx/middleware';
import { createCredentialsSessionEpicDependencies } from 'rdx/session/createCredentialsSessionEpicDependencies';
import { createReduxStore } from 'rdx/store';
import { ApplicationUpdateService } from 'services/ApplicationUpdateService';
import { initSentry } from 'services/sentry';
import { initializeGTMSnippet } from 'utils/gtagSnippet';

import { AppServicesContext } from 'components/AppServicesProvider/AppServicesProvider';

import { DateFilterService } from '@provider-portal/common/services/DateFilterService';
import { SessionService } from '@provider-portal/common/services/SessionService';
import { TargetingService } from '@provider-portal/common/services/TargetingService';
import { createObservableAndStateFromGlobalStore } from './adapters';
import { commonAccountAdapter } from './adapters/CommonAccountAdapter';
import { urlLocationAdapter } from './adapters/UrlLocationAdapter';
import { AppContainer } from './AppContainer';
import { ThemeListener } from './ThemeListener';

import '@provider-portal/common/styles/index.css';
import '@provider-portal/design-common/clay/clay.compiled.css';
import '@provider-portal/design-common/clay/fonts/inter.css';

initSentry(config);

const initAppProps = (
  jwtHttpClient: JWTHttpClient,
  unauthenticatedHttpClient: UnauthenticatedHttpClient,
) => {
  const apiClient = new ApiClient(jwtHttpClient, unauthenticatedHttpClient);
  const reduxSession = createCredentialsSessionEpicDependencies(apiClient);
  const mixpanelService = new MixpanelService();
  const eventAliasingService = new EventAliasingService(mixpanelService);
  const applicationUpdateService = new ApplicationUpdateService();

  const appServices: AppServices = {
    mixpanelService,
    eventAliasingService,
    config,
    reduxSession,
    apiClient,
    applicationUpdateService,
  };
  const { store, persistor, history } = createReduxStore(appServices);

  const gatewaySelectors = createGatewaySelectors({ store, config });

  const accountChangeObservable = createObservableAndStateFromGlobalStore(
    store,
    commonAccountAdapter,
    true,
  );
  const locationChangeObservable = createObservableAndStateFromGlobalStore(
    store,
    urlLocationAdapter,
    false,
  );

  const accountService = new AccountService(accountChangeObservable);
  const trackingService = new TrackingService(accountService);
  const sessionService = new SessionService(store, apiClient);
  const dateFilterService = new DateFilterService();
  const targetingService = new TargetingService(store);
  const brazeService = new BrazeService(store, targetingService, accountService, mixpanelService);

  const commonAppServices = new CommonApplicationServices(
    accountService,
    appServices.apiClient,
    appServices.mixpanelService,
    trackingService,
    appServices.eventAliasingService,
    new NotificationService(store),
    targetingService,
    sessionService,
    dateFilterService,
    brazeService,
    config,
  );

  return {
    store,
    persistor,
    history,
    commonAppServices,
    locationChangeObservable,
    appServices,
    gatewaySelectors,
    applicationUpdateService,
  };
};

const jwtHttpClient = new HttpClientBoltServer<JWTClientConstraints>({
  baseUrl: config.apiBaseUrl,
});
const unauthenticatedHttpClient = new HttpClientBoltServer<UnauthenticatedClientConstraints>({
  baseUrl: config.apiBaseUrl,
});
const appProps = initAppProps(jwtHttpClient, unauthenticatedHttpClient);

export interface ProviderPortalAppService {
  publisher: Facade.Publisher;
  navigationService: Facade.GlobalNavigationService;
  moduleManifests: ProviderPortalModuleManifest[];
}

export interface ProviderPortalModuleManifest extends Facade.ModuleManifest {
  isPublic: boolean;
}

interface AppEntryProps {
  moduleManifests: ProviderPortalModuleManifest[];
  navigationService: ProviderPortalNavigationService;
  children?: React.ReactNode;
}

const AppEntry: React.FC<AppEntryProps> = (props) => {
  const [isReady, setReadyState] = useState(false);

  const initAppDeps = async () => {
    setReadyState(true);
  };

  return (
    <AppServicesContext.Provider value={appProps.appServices}>
      <Sentry.ErrorBoundary>
        <Provider store={appProps.store}>
          <ThemeListener />
          <PersistGate loading={null} persistor={appProps.persistor} onBeforeLift={initAppDeps}>
            {isReady ? (
              <AppContainer
                moduleManifests={props.moduleManifests}
                history={appProps.history}
                navigationService={props.navigationService}
                commonAppServices={appProps.commonAppServices}
              >
                {props.children}
              </AppContainer>
            ) : (
              <FullViewSpinner size="48px" />
            )}
          </PersistGate>
        </Provider>
      </Sentry.ErrorBoundary>
    </AppServicesContext.Provider>
  );
};

export class ProviderPortalApp extends App {
  private services!: ProviderPortalAppService;
  private providerPortalNavService!: ProviderPortalNavigationService;

  protected jwtHttpClient: HttpClientBoltServer<JWTClientConstraints>;
  protected unauthenticatedHttpClient: HttpClientBoltServer<UnauthenticatedClientConstraints>;

  onModuleLoaded(
    moduleConstructor: new (options: ProviderPortalModuleConfig) => ProviderPortalModuleSetup,
    moduleConfig: ModuleConfig,
  ) {
    const ModuleClassConstructor = moduleConstructor;

    new ModuleClassConstructor({
      ...moduleConfig,
      latticeNavigationService: this.services.navigationService,
      navigationService: this.providerPortalNavService,
      publisher: this.services.publisher,
      commonAppServices: appProps.commonAppServices,
    });
  }

  public init(providerPortalAppService: ProviderPortalAppService): void {
    this.services = providerPortalAppService;

    this.jwtHttpClient = jwtHttpClient;
    this.unauthenticatedHttpClient = unauthenticatedHttpClient;

    this.providerPortalNavService = new ProviderPortalNavigationService(
      this.services.navigationService,
      appProps.locationChangeObservable,
    );
    if (process.env.REACT_APP_STAGE === 'live') {
      initializeGTMSnippet({
        tagId: 'GTM-WGNQRNT',
      });
    }

    this.jwtHttpClient.init({
      authStrategy: {
        jwt: {
          getAuthToken: appProps.gatewaySelectors.getAccessToken,
          getExtraConfig() {
            return {
              updateAuthToken: (): Promise<string> => {
                return waitForAccessTokenUpdate(appProps.store).catch((e) => {
                  if (e.message === 'REFRESH_TOKEN_INVALID') {
                    // user's refresh token is invalid
                    // Most likely cause is that they logged in using a different browser/device
                    //  and then returned to this browser.
                    return ''; // swallow the error, user is redirected to login page anyway
                  }

                  throw e;
                });
              },
              accessTokenBlockingTimeoutBeforeExpiryInSeconds:
                config.accessTokenBlockingTimeoutBeforeExpiryInSeconds,
              accessTokenRefreshTimeoutBeforeExpiryInSeconds:
                config.accessTokenRefreshTimeoutBeforeExpiryInSeconds,
            };
          },
        },
      },
      standardRequestStrategy: {
        getStandardRequest: appProps.gatewaySelectors.getDefaultQueryParams,
      },
    });

    this.jwtHttpClient.useInterceptors([createAuthInvalidatedInterceptor(appProps.store)]);

    this.unauthenticatedHttpClient.init({
      standardRequestStrategy: {
        getStandardRequest: appProps.gatewaySelectors.getDefaultQueryParams,
      },
    });

    super.init(providerPortalAppService);
  }

  render(props: RenderProps) {
    return (
      <AppEntry
        moduleManifests={this.services.moduleManifests}
        navigationService={this.providerPortalNavService}
      >
        {props.children}
      </AppEntry>
    );
  }

  /*
   * Lattice doesn't support provider-portal like Crowdin based translations at the moment.
   * PRs welcomed! :)
   */
  getTranslations() {
    return {};
  }
}
